/**
 * DATAT BAKOM LAGET — det attest och inhyrda räknar ur.
 *
 * VARFÖR EN HEL PULL. Attest behöver week_status, member, time_entry, mileage,
 * expense och absence för EN vecka; inhyrda behöver member, work_order,
 * work_order_guest och time_entry för en period. Ingen av de frågorna har en
 * egen rutt — kontraktet har EN läskanal för företagsdata, SYNC_PULL, och den
 * är en DELTA-ström med en markör. Utan den lokala databasen (steg 5 äger
 * utkorgen, IndexedDB och bakgrundssynken) finns inget att lägga deltat på, så
 * skärmarna hämtar hela firman från since = 0 vid varje besök och håller den i
 * minnet. För en firma med fem man är det några tusen rader.
 *
 * NÄR STEG 5 FINNS byts `hamtaAllt` mot en läsning ur den lokala databasen och
 * INGEN skärm behöver ändras: båda skärmarna ser bara `Firmadata`.
 *
 * MARKÖREN FÖLJS ORDAGRANT (SyncPullResponse): next_since är högsta LÄSTA seq,
 * även för rader vyn filtrerade bort, och has_more = true betyder BARA att fler
 * seq återstår — inte att changes är icke-tom. Loopen går därför på has_more och
 * aldrig på "kom det några rader".
 *
 * MJUKRADERAT är borta HÄR. Varje lista filtreras på deleted_at === null, så att
 * ingen skärm behöver komma ihåg det. En mjukraderad rad räknas som befintlig i
 * versionsjämförelsen men som obefintlig överallt annars (shared/types.ts).
 */

import { useEffect, useState } from 'react';
import { SYNC_PULL } from '@delat/routes';
import { INVOICE_BASIS_MAX_DAYS, SYNC_PULL_MAX_LIMIT, asSeq } from '@delat/types';
import type {
  AbsenceRow,
  CompanyId,
  ExpenseRow,
  MemberRow,
  MileageRow,
  Seq,
  TimeEntryRow,
  WeekStatusRow,
  WorkDate,
  WorkOrderGuestRow,
  WorkOrderRow,
} from '@delat/types';
import { apifel, klienten, lyckades, misslyckades } from '../api';
import type { Klartidfel, Resultat } from '../api';
import { granskaSyncPull } from '../api/granskare/laget';
import { dagarMellan, idag, mandagen, plusVeckor, veckaFor } from '../app/datum';
import type { Segmentval } from '../ui';

/* == Perioden ============================================================
   VECKAN ÄR ENHETEN (beslut 04), även när skärmen visar flera. Intervallen
   nedan börjar därför på en MÅNDAG och slutar i dag — aldrig "denna månad",
   som inte finns i produkten. Det längsta ligger inom INVOICE_BASIS_MAX_DAYS.
   ======================================================================== */

export type Periodval = '4v' | '12v' | '52v';

export const PERIODVALEN: readonly Segmentval<Periodval>[] = [
  { varde: '4v', etikett: '4 veckor' },
  { varde: '12v', etikett: '12 veckor' },
  { varde: '52v', etikett: '52 veckor' },
];

const VECKOR_I: Readonly<Record<Periodval, number>> = { '4v': 4, '12v': 12, '52v': 52 };

export interface Period {
  readonly from_date: WorkDate;
  readonly to_date: WorkDate;
  readonly text: string;
}

export const periodFor = (val: Periodval, nu: WorkDate = idag()): Period => {
  const veckor = VECKOR_I[val];
  const from_date = mandagen(plusVeckor(veckaFor(nu), -(veckor - 1)));
  // Sanity: 52 veckor bakåt från en måndag är högst 363 dagar, alltså innanför
  // INVOICE_BASIS_MAX_DAYS. Raden står kvar så att en ändring av tabellen ovan
  // upptäcks här och inte som ett VALIDATION_FAILED { field: 'to_date' }.
  if (dagarMellan(from_date, nu) + 1 > INVOICE_BASIS_MAX_DAYS) {
    throw new Error(`perioden ${val} är längre än INVOICE_BASIS_MAX_DAYS`);
  }
  return { from_date, to_date: nu, text: `${veckor} veckor` };
};

/* == Firmans data ======================================================== */

export interface Firmadata {
  readonly medlemmar: readonly MemberRow[];
  readonly veckolagen: readonly WeekStatusRow[];
  readonly tidrader: readonly TimeEntryRow[];
  readonly milrader: readonly MileageRow[];
  readonly utlagg: readonly ExpenseRow[];
  readonly franvaro: readonly AbsenceRow[];
  readonly arbetsordrar: readonly WorkOrderRow[];
  readonly gasttilldelningar: readonly WorkOrderGuestRow[];
  /** Markören vi läst fram till. Steg 5 fortsätter härifrån. */
  readonly hamtat_till: Seq;
}

export type Firmalage =
  | { readonly status: 'laddar' }
  | { readonly status: 'fel'; readonly fel: Klartidfel }
  | { readonly status: 'klar'; readonly data: Firmadata };

/**
 * Taket är en spärr mot en server som svarar has_more i evighet, inte en
 * datagräns: SYNC_PULL_MAX_LIMIT rader per varv gånger taket räcker till en
 * firma med två miljoner rader.
 */
const MAX_VARV = 2000;

const levande = <T extends { readonly deleted_at: number | null }>(
  rader: readonly T[],
): readonly T[] => rader.filter((r) => r.deleted_at === null);

export const hamtaAllt = async (
  firmaId: CompanyId,
  signal: AbortSignal,
): Promise<Resultat<Firmadata>> => {
  const medlemmar: MemberRow[] = [];
  const veckolagen: WeekStatusRow[] = [];
  const tidrader: TimeEntryRow[] = [];
  const milrader: MileageRow[] = [];
  const utlagg: ExpenseRow[] = [];
  const franvaro: AbsenceRow[] = [];
  const arbetsordrar: WorkOrderRow[] = [];
  const gasttilldelningar: WorkOrderGuestRow[] = [];

  let since = asSeq(0);

  for (let varv = 0; varv < MAX_VARV; varv += 1) {
    const svar = await klienten.anrop(
      SYNC_PULL,
      {
        params: { company_id: firmaId },
        data: { since, limit: SYNC_PULL_MAX_LIMIT },
        signal,
      },
      granskaSyncPull,
    );
    if (!svar.ok) return misslyckades(svar.fel);

    const pull = svar.varde;
    if (pull.view !== 'full') {
      // Rollen läses ur D1 vid varje anrop, så det HÄR är en degraderad chef:
      // hans bevis dog inte, men han får inte längre se laget. FORBIDDEN är
      // exakt det beskedet, och sessionen laddar om ME nästa gång.
      return misslyckades(apifel('FORBIDDEN', undefined, ''));
    }

    const c = pull.changes;
    medlemmar.push(...c.member);
    veckolagen.push(...c.week_status);
    tidrader.push(...c.time_entry);
    milrader.push(...c.mileage);
    utlagg.push(...c.expense);
    franvaro.push(...c.absence);
    arbetsordrar.push(...c.work_order);
    gasttilldelningar.push(...c.work_order_guest);

    if (!pull.has_more) {
      return lyckades({
        medlemmar: levande(medlemmar),
        veckolagen: levande(veckolagen),
        tidrader: levande(tidrader),
        milrader: levande(milrader),
        utlagg: levande(utlagg),
        franvaro: levande(franvaro),
        arbetsordrar: levande(arbetsordrar),
        gasttilldelningar: levande(gasttilldelningar),
        hamtat_till: pull.next_since,
      });
    }
    if (Number(pull.next_since) <= Number(since)) {
      // Markören står still trots has_more. Då är det serverns räkning som är
      // trasig, och en loop till hade snurrat i evighet på samma fönster.
      return misslyckades(apifel('SERVER_ERROR', undefined, ''));
    }
    since = pull.next_since;
  }
  return misslyckades(apifel('SERVER_ERROR', undefined, ''));
};

/**
 * Hela firman, laddad om när `omgang` ändras. En lyckad SYNC_PUSH följs ALLTID
 * av en pull (shared/types.ts avsnitt 8: "Push-svaret bär ALDRIG radinnehåll"),
 * och det är precis vad en bumpad omgång gör här.
 */
export const useFirmadata = (firmaId: CompanyId, omgang: number): Firmalage => {
  const [lage, sattLage] = useState<Firmalage>({ status: 'laddar' });

  useEffect(() => {
    const styrning = new AbortController();
    let aktuell = true;
    sattLage({ status: 'laddar' });

    void (async () => {
      const svar = await hamtaAllt(firmaId, styrning.signal);
      if (!aktuell) return;
      sattLage(svar.ok ? { status: 'klar', data: svar.varde } : { status: 'fel', fel: svar.fel });
    })();

    return () => {
      aktuell = false;
      styrning.abort();
    };
  }, [firmaId, omgang]);

  return lage;
};

/* == Små texthjälpare de tre skärmarna delar ============================= */

/** '5593026494' -> '559302-6494'. Bindestrecket är det människor läser. */
export const visaOrgnummer = (nummer: string): string =>
  nummer.length === 10 ? `${nummer.slice(0, 6)}-${nummer.slice(6)}` : nummer;

/** "1 person" / "3 personer". */
export const visaPersoner = (antal: number): string =>
  antal === 1 ? '1 person' : `${antal} personer`;

/** "1 dag" / "5 dagar". */
export const visaDagar = (antal: number): string =>
  antal === 1 ? '1 dag' : `${antal} dagar`;
